import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowUpRight, Loader2, RefreshCw, X } from 'lucide-react';
import { deckMachineRequest, deleteDeckMachine, requestAgentBridgeUrl } from '../../services/api';

/*
 * Agent incidents: every agent tool failure across all clients, as the builder
 * records it in agent_incidents. The super admin filters them, opens one to
 * read the full error, leaves a note and marks it seen, fixed or ignored. The
 * Notifications panel sets where new incidents are announced: one Slack channel
 * and a list of email recipients. Rendered inside the Command Deck, so it uses
 * the deck's own cd- classes.
 */

type IncidentStatus = 'open' | 'seen' | 'fixed' | 'ignored';

interface Incident {
  id: string;
  workspaceId: string | null;
  workspaceName: string | null;
  projectId: string | null;
  projectTitle: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  agent: string | null;
  tool: string | null;
  errorText: string | null;
  count: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  status: IncidentStatus;
  note: string | null;
}

interface IncidentPage {
  available?: boolean;
  reason?: string;
  incidents?: Incident[];
  nextCursor?: string | null;
}

interface NotificationSettings {
  available: boolean;
  reason?: string;
  supported?: boolean;
  slack?: { connected: boolean; channelName: string | null; teamName: string | null } | null;
  emails?: string[];
}

const STATUS_OPTIONS: Array<[string, string]> = [
  ['open', 'Open'],
  ['seen', 'Seen'],
  ['fixed', 'Fixed'],
  ['ignored', 'Ignored'],
  ['', 'All statuses'],
];

const STATUS_CHIP: Record<IncidentStatus, string> = { open: 'r', seen: 'w', fixed: 'g', ignored: 'o' };
const STATUS_LABEL: Record<IncidentStatus, string> = { open: 'Open', seen: 'Seen', fixed: 'Fixed', ignored: 'Ignored' };

const ACTIONS: Array<[IncidentStatus, string]> = [
  ['seen', 'Mark seen'],
  ['fixed', 'Mark fixed'],
  ['ignored', 'Ignore'],
];

const PAGE_SIZE = '50';

const errorText = (err: unknown, fallback: string) => {
  const detail = (err as any)?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const when = (iso: string | null) => {
  if (!iso) return 'Unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const ago = (iso: string | null) => {
  if (!iso) return 'Unknown';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Unknown';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const firstLine = (text: string | null) => (text || '').split('\n').find((l) => l.trim()) || 'No error text';

// The builder answers available:false with a reason when it cannot be reached;
// a 404 means it is reachable but does not serve this endpoint yet.
const unavailableNote = (reason: string | undefined, what: string) => {
  if (reason === 'builder 404') return <><b>Not on the builder yet.</b> The builder does not serve {what} yet. This fills in once that release is live.</>;
  if (reason === 'not configured') return <><b>Builder not configured.</b> Set the builder URL and provisioning key on this server to see {what}.</>;
  return <><b>Builder unreachable</b> ({reason || 'unknown'}). {what.charAt(0).toUpperCase() + what.slice(1)} live on the builder and appear once it answers.</>;
};

const CSS = `
.ai-filters{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.ai-err{max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:11.5px;color:var(--cd-muted)}
.ai-pre{white-space:pre-wrap;word-break:break-word;font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:11.5px;line-height:1.5;background:var(--cd-bg);border:1px solid var(--cd-line);border-radius:8px;padding:10px 12px;max-height:320px;overflow:auto;margin:0}
.ai-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:14px}
.ai-emails{display:flex;flex-wrap:wrap;gap:6px;padding:0 15px 10px}
.ai-chip-x{border:0;background:transparent;color:inherit;cursor:pointer;padding:0;display:inline-flex;align-items:center}
.ai-add{display:flex;gap:8px;align-items:center;padding:0 15px 12px;flex-wrap:wrap}
.ai-row{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:10px 15px;flex-wrap:wrap}
`;

const NotificationsPanel = () => {
  const [st, setSt] = useState<NotificationSettings | null>(null);
  const [failed, setFailed] = useState('');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkError, setLinkError] = useState('');

  const load = useCallback(async () => {
    setFailed('');
    try {
      setSt(await deckMachineRequest('get', 'agent-incidents/notifications'));
    } catch (err) {
      setFailed(errorText(err, 'Could not load notification settings.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveEmails = async (emails: string[], done: string) => {
    setBusy(true);
    try {
      await deckMachineRequest('put', 'agent-incidents/notifications', { emails });
      toast.success(done);
      await load();
      return true;
    } catch (err) {
      toast.error(errorText(err, 'Could not save the email list'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const email = draft.trim().toLowerCase();
    if (!email) return;
    const current = st?.emails || [];
    if (current.indexOf(email) !== -1) { setDraft(''); return; }
    if (await saveEmails(current.concat(email), `${email} will get incident emails`)) setDraft('');
  };

  const remove = (email: string) =>
    saveEmails((st?.emails || []).filter((e) => e !== email), `${email} removed`);

  const disconnect = async () => {
    setBusy(true);
    try {
      await deleteDeckMachine('notifications', { type: 'AGENT_INCIDENT' });
      toast.success('Slack channel disconnected');
      await load();
    } catch (err) {
      toast.error(errorText(err, 'Could not disconnect the channel'));
    } finally {
      setBusy(false);
    }
  };

  // The tab opens before the await: a window.open after it is treated as a popup and blocked.
  const openSlackSetup = async () => {
    setLinkError('');
    const tab = window.open('', '_blank');
    try {
      const { url } = await requestAgentBridgeUrl('/admin/settings');
      if (!url) { tab?.close(); return; }
      if (!tab) { window.location.assign(url); return; }
      tab.opener = null;
      tab.location.href = url;
    } catch (err) {
      tab?.close();
      setLinkError(errorText(err, 'Could not open the builder.'));
    }
  };

  let body: React.ReactNode;
  if (failed) {
    body = <div className="cd-note" style={{ margin: 15 }}><b>Couldn’t load notification settings.</b> {failed}</div>;
  } else if (!st) {
    body = <div style={{ padding: 20, display: 'grid', placeItems: 'center' }}><Loader2 className="animate-spin" size={16} style={{ color: 'var(--cd-mut2)' }} /></div>;
  } else if (!st.available) {
    body = <div className="cd-note" style={{ margin: 15 }}>{unavailableNote(st.reason, 'notification settings')}</div>;
  } else if (!st.supported) {
    body = <div className="cd-note" style={{ margin: 15 }}><b>Not on the builder yet.</b> The builder does not offer agent incident alerts yet. Slack and email settings appear here once it does.</div>;
  } else {
    const slack = st.slack;
    const emails = st.emails || [];
    body = (
      <>
        <div className="ai-row">
          <div>
            <div style={{ fontWeight: 600, fontSize: 12.5 }}>Slack channel</div>
            <div className="cd-sub2">New incidents are posted here.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {slack?.connected
              ? <span className="cd-chip g">#{slack.channelName}{slack.teamName ? ` · ${slack.teamName}` : ''}</span>
              : <span className="cd-chip o">Not connected</span>}
            {slack?.connected
              ? <button type="button" className="cd-btn sm" disabled={busy} onClick={disconnect}>Disconnect</button>
              : <button type="button" className="cd-btn sm" onClick={openSlackSetup}>Connect in the builder <ArrowUpRight size={13} /></button>}
          </div>
        </div>
        {linkError && <div className="cd-sub2" style={{ padding: '0 15px 8px', color: 'var(--cd-red)' }}>{linkError}</div>}
        <div style={{ borderTop: '1px solid var(--cd-line)', padding: '10px 15px 8px' }}>
          <div style={{ fontWeight: 600, fontSize: 12.5 }}>Email recipients</div>
          <div className="cd-sub2">Each address gets an email for every new incident.</div>
        </div>
        <div className="ai-emails">
          {emails.length === 0 && <span className="cd-sub2">No one gets incident emails yet.</span>}
          {emails.map((e) => (
            <span key={e} className="cd-chip b">
              {e}
              <button type="button" className="ai-chip-x" aria-label={`Remove ${e}`} disabled={busy} onClick={() => remove(e)}><X size={11} /></button>
            </span>
          ))}
        </div>
        <form className="ai-add" onSubmit={(ev) => { ev.preventDefault(); add(); }}>
          <input className="cd-finput" type="email" placeholder="name@company.com" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <button type="submit" className="cd-btn sm" disabled={busy || !draft.trim()}>Add</button>
        </form>
      </>
    );
  }

  return (
    <div className="cd-panel">
      <div className="cd-ph"><span className="t">Notifications</span><span className="cd-sub2">Where new agent incidents are announced</span></div>
      {body}
      <div className="cd-cap">Changes are saved on the builder and recorded under your name. Connecting a Slack channel runs Slack’s own install flow in builder settings.</div>
    </div>
  );
};

const IncidentDrawer = ({ incident, onClose, onSaved }: {
  incident: Incident;
  onClose: () => void;
  onSaved: (next: Incident) => void;
}) => {
  const [note, setNote] = useState(incident.note || '');
  const [busy, setBusy] = useState('');
  useEffect(() => { setNote(incident.note || ''); }, [incident.id, incident.note]);

  const update = async (body: { status?: IncidentStatus; note?: string | null }, key: string, done: string) => {
    setBusy(key);
    try {
      const r = await deckMachineRequest('patch', `agent-incidents/${encodeURIComponent(incident.id)}`, body);
      onSaved({ ...incident, ...body, ...(r?.incident || {}) });
      toast.success(done);
    } catch (err) {
      toast.error(errorText(err, 'Could not update the incident'));
    } finally {
      setBusy('');
    }
  };

  const noteChanged = note.trim() !== (incident.note || '').trim();

  return (
    <>
      <div className="cd-drawer-scrim" onClick={() => !busy && onClose()} />
      <div className="cd-drawer" role="dialog" aria-label="Incident detail">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="cd-eyebrow">Agent incident</div>
          <button type="button" className="cd-btn sm" onClick={onClose} style={{ padding: '4px 8px' }} aria-label="Close"><X size={14} /></button>
        </div>
        <h2 style={{ margin: '4px 0 2px', fontSize: 19, letterSpacing: '-.02em', wordBreak: 'break-word' }}>{incident.tool || 'Unknown tool'}</h2>
        <div className="cd-sub2">{incident.agent || 'Unknown agent'} · {incident.workspaceName || 'Unknown client'}</div>
        <div style={{ marginTop: 10 }}><span className={`cd-chip ${STATUS_CHIP[incident.status] || 'o'}`}>{STATUS_LABEL[incident.status] || incident.status}</span></div>

        <table className="cd-t" style={{ margin: '14px 0' }}><tbody>
          <tr><td className="cd-sub2">Client</td><td className="r">{incident.workspaceName || 'Unknown'}</td></tr>
          <tr><td className="cd-sub2">Project</td><td className="r">{incident.projectTitle || 'No project'}</td></tr>
          <tr><td className="cd-sub2">User</td><td className="r">{incident.userName || incident.userEmail || 'Unknown'}{incident.userName && incident.userEmail ? <div className="cd-sub2">{incident.userEmail}</div> : null}</td></tr>
          <tr><td className="cd-sub2">Times seen</td><td className="r m">{Number(incident.count || 0).toLocaleString()}</td></tr>
          <tr><td className="cd-sub2">First seen</td><td className="r">{when(incident.firstSeenAt)}</td></tr>
          <tr><td className="cd-sub2">Last seen</td><td className="r">{when(incident.lastSeenAt)}</td></tr>
        </tbody></table>

        <label className="cd-label" htmlFor="ai-error">Full error</label>
        <pre id="ai-error" className="ai-pre">{incident.errorText || 'No error text was recorded.'}</pre>

        <label className="cd-label" htmlFor="ai-note">Note</label>
        <textarea id="ai-note" className="cd-textarea" style={{ fontFamily: 'inherit' }} value={note}
          onChange={(e) => setNote(e.target.value)} placeholder="What you found, or what was done about it" />
        <div style={{ marginTop: 8 }}>
          <button type="button" className="cd-btn sm" disabled={!!busy || !noteChanged}
            onClick={() => update({ note: note.trim() || null }, 'note', 'Note saved')}>
            {busy === 'note' ? 'Saving…' : 'Save note'}
          </button>
        </div>

        <div className="ai-actions">
          {ACTIONS.map(([status, label]) => (
            <button key={status} type="button" className={`cd-btn${status === 'fixed' ? ' pri' : ''}`}
              disabled={!!busy || incident.status === status}
              onClick={() => update(noteChanged ? { status, note: note.trim() || null } : { status }, status, `Marked ${STATUS_LABEL[status].toLowerCase()}`)}>
              {busy === status ? 'Saving…' : label}
            </button>
          ))}
        </div>
        <div className="cd-foot">Status changes and notes are saved on the builder and recorded under your name.</div>
      </div>
    </>
  );
};

const AgentIncidentsTab = () => {
  // A Slack or email alert links here with ?incident=<id>: list every status
  // so the incident is found even once someone has marked it, then open it.
  const [linked, setLinked] = useState(() => new URLSearchParams(window.location.search).get('incident') || '');
  const [status, setStatus] = useState(linked ? '' : 'open');
  const [client, setClient] = useState('');
  const [agent, setAgent] = useState('');
  const [page, setPage] = useState<IncidentPage | null>(null);
  const [failed, setFailed] = useState('');
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState<Incident | null>(null);
  // Filter choices are learned from the incidents seen so far, so narrowing to
  // one client does not empty the client list.
  const [clients, setClients] = useState<Record<string, string>>({});
  const [agents, setAgents] = useState<string[]>([]);

  const remember = (rows: Incident[]) => {
    setClients((prev) => {
      const next = { ...prev };
      rows.forEach((r) => { if (r.workspaceId) next[r.workspaceId] = r.workspaceName || r.workspaceId; });
      return next;
    });
    setAgents((prev) => {
      const next = prev.slice();
      rows.forEach((r) => { if (r.agent && next.indexOf(r.agent) === -1) next.push(r.agent); });
      return next.sort();
    });
  };

  const params = useCallback((cursor?: string) => {
    const p: Record<string, string> = { limit: PAGE_SIZE };
    if (status) p.status = status;
    if (client) p.workspace_id = client;
    if (agent) p.agent = agent;
    if (cursor) p.cursor = cursor;
    return p;
  }, [status, client, agent]);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed('');
    try {
      const d: IncidentPage = await deckMachineRequest('get', 'agent-incidents', undefined, params());
      setPage(d);
      if (d.available !== false) remember(d.incidents || []);
    } catch (err) {
      setFailed(errorText(err, 'Could not load agent incidents.'));
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!linked || !page?.incidents) return;
    const hit = page.incidents.find((r) => r.id === linked);
    if (hit) setOpen(hit);
    setLinked('');
  }, [linked, page]);

  const loadMore = async () => {
    if (!page?.nextCursor || more) return;
    setMore(true);
    try {
      const d: IncidentPage = await deckMachineRequest('get', 'agent-incidents', undefined, params(page.nextCursor));
      if (d.available === false) { toast.error('The builder stopped answering. Try again shortly.'); return; }
      remember(d.incidents || []);
      setPage((p) => ({ ...p, incidents: (p?.incidents || []).concat(d.incidents || []), nextCursor: d.nextCursor }));
    } catch (err) {
      toast.error(errorText(err, 'Could not load more incidents'));
    } finally {
      setMore(false);
    }
  };

  const saved = (next: Incident) => {
    setOpen(next);
    setPage((p) => p && ({ ...p, incidents: (p.incidents || []).map((r) => (r.id === next.id ? next : r)) }));
  };

  const rows = page?.incidents || [];
  const clientIds = Object.keys(clients).sort((a, b) => clients[a].localeCompare(clients[b]));

  let body: React.ReactNode;
  if (failed) {
    body = <div className="cd-note" style={{ margin: 15 }}><b>Couldn’t load agent incidents.</b> {failed}</div>;
  } else if (!page && loading) {
    body = <div style={{ padding: 30, display: 'grid', placeItems: 'center' }}><Loader2 className="animate-spin" size={16} style={{ color: 'var(--cd-mut2)' }} /></div>;
  } else if (page?.available === false) {
    body = <div className="cd-note" style={{ margin: 15 }}>{unavailableNote(page.reason, 'agent incidents')}</div>;
  } else {
    body = (
      <div style={{ overflowX: 'auto' }}>
        <table className="cd-t">
          <thead><tr>
            <th>Client</th><th>Project</th><th>Agent</th><th>Tool</th><th>User</th><th>Error</th>
            <th className="r">Count</th><th>Last seen</th><th>Status</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="cd-sub2">
                {status || client || agent ? 'No incidents match these filters.' : 'No agent incidents recorded.'}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="cl" onClick={() => setOpen(r)} tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setOpen(r); }}>
                <td>{r.workspaceName || <span className="cd-sub2">Unknown</span>}</td>
                <td>{r.projectTitle || <span className="cd-sub2">No project</span>}</td>
                <td><span className="cd-chip o">{r.agent || 'unknown'}</span></td>
                <td className="m">{r.tool || <span className="cd-sub2">Unknown</span>}</td>
                <td>{r.userName || r.userEmail || <span className="cd-sub2">Unknown</span>}</td>
                <td><div className="ai-err" title={firstLine(r.errorText)}>{firstLine(r.errorText)}</div></td>
                <td className="r m">{Number(r.count || 0).toLocaleString()}</td>
                <td className="cd-sub2" title={when(r.lastSeenAt)}>{ago(r.lastSeenAt)}</td>
                <td><span className={`cd-chip ${STATUS_CHIP[r.status] || 'o'}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {page?.nextCursor && (
          <div style={{ padding: '10px 15px', borderTop: '1px solid var(--cd-line)' }}>
            <button type="button" className="cd-btn sm" disabled={more} onClick={loadMore}>{more ? 'Loading…' : 'Load more'}</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div style={{ marginBottom: 14 }}>
        <div className="cd-eyebrow">Monitor</div>
        <div className="cd-h1">Agent incidents</div>
        <div className="cd-sub">Every tool failure an agent hit, across all clients. Repeats of the same failure are counted on one row. Open one to read the full error and mark it seen, fixed or ignored.</div>
      </div>

      <div className="cd-panel">
        <div className="cd-ph">
          <div className="ai-filters">
            <select className="cd-sel" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map(([v, l]) => <option key={v || 'all'} value={v}>{l}</option>)}
            </select>
            <select className="cd-sel" aria-label="Client" value={client} onChange={(e) => setClient(e.target.value)}>
              <option value="">All clients</option>
              {clientIds.map((id) => <option key={id} value={id}>{clients[id]}</option>)}
            </select>
            <select className="cd-sel" aria-label="Agent" value={agent} onChange={(e) => setAgent(e.target.value)}>
              <option value="">All agents</option>
              {agents.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <button type="button" className="cd-btn sm" disabled={loading} onClick={load}>
            {loading ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
        {body}
      </div>

      <NotificationsPanel />

      {open && <IncidentDrawer incident={open} onClose={() => setOpen(null)} onSaved={saved} />}
    </>
  );
};

export default AgentIncidentsTab;
